import { createContext, useContext, ReactNode } from 'react';
import { CommunityId } from '../model/community.types';
import { canEditContent } from './canEditContent';

// Community-scoped values shared by every node under the active community
// (identity + permissions). Post-specific data (post, postId, pinnedCount)
// stays in props; these are constant across the whole community subtree.
type CommunityContextValue = {
  communityId: CommunityId;
  influencerId?: string;
  canModerate: boolean;
  isDmFeatureEnabled: boolean;
  // Per-author edit permission. Lives next to canModerate so every consumer
  // asks the same place instead of deriving the rule itself.
  canEditContent: (isOwn: boolean, isCreator: boolean) => boolean;
};

const CommunityContext = createContext<CommunityContextValue | null>(null);

type ProviderProps = Omit<CommunityContextValue, 'canEditContent'> & {
  children: ReactNode;
};

export const CommunityProvider = ({ children, ...value }: ProviderProps) => (
  <CommunityContext.Provider value={{ ...value, canEditContent }}>
    {children}
  </CommunityContext.Provider>
);

export const useCommunityContext = (): CommunityContextValue => {
  const value = useContext(CommunityContext);
  if (value === null) {
    throw new Error(
      'useCommunityContext must be used within a CommunityProvider',
    );
  }
  return value;
};
